import cx from 'classnames';
import { slice, toString, xor } from 'lodash';
import { VariantLabel } from 'pages/documents_v2/views/edit_area/blocks/VariantLabel';
import HelperFunctions from 'pages/global/HelperFunctions';
import { useMemo, useState } from 'react';
import { ExclamationTriangleFill } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { ArrayParam, useQueryParam } from 'use-query-params';

/**
 * @param {number[]} blockVariants
 * @param {number[]} areaVariants
 * @param {import('models/documents.models').DocumentVariant[]} documentVariants
 * @param {import('models/documents.models').DocumentVariant} activeVariant
 * @param {boolean} isTemplate
 * @param {boolean} toggleEnabled
 * @param {number} showLimit
 */
export function VariantsAsLabels({
    blockVariants,
    areaVariants,
    documentVariants,
    activeVariant,
    isTemplate = false,
    toggleEnabled = true,
    showLimit = 30,
}) {
    const { t } = useTranslation();
    const [showAll, setShowAll] = useState(false);
    const [selectedVariants = [], setSelectedVariants] = useQueryParam('highligh_variants', ArrayParam);

    const areaDocumentVariants = useMemo(() => {
        if (areaVariants.length === 0) {
            return [];
        }

        return documentVariants.filter(
            (variant) => !variant.variantGroup && variant.parentId !== null && areaVariants.includes(variant.id),
        );
    }, [documentVariants, areaVariants]);

    const allVariants = useMemo(() => {
        return areaDocumentVariants
            .filter((variant) => blockVariants.includes(variant.id))
            .map((variant) => {
                return {
                    ...variant,
                    parent: documentVariants.find((_variant) => _variant.id === variant.parentId),
                };
            });
    }, [blockVariants, areaDocumentVariants, documentVariants]);

    const activeVariantInSelection =
        activeVariant && activeVariant.parentId !== null && selectedVariants.includes(toString(activeVariant.id));
    const limit = selectedVariants.length > 0 || activeVariantInSelection ? 9999999 : showLimit;
    const variants = showAll ? allVariants : slice(allVariants, 0, limit);
    const showMoreButton = !showAll && allVariants.length > limit;

    return (
        <div
            className={cx('d-flex flex-wrap', {
                'variant-labels-small': allVariants.length > 5,
            })}
        >
            {variants.map((variant, index) => (
                <VariantLabel
                    variant={variant}
                    active={selectedVariants.includes(toString(variant.id))}
                    toggleVariant={toggleVariant}
                    toggleEnabled={toggleEnabled}
                    key={`block-${index}-variant-${variant.id}`}
                />
            ))}

            {showMoreButton && (
                <span
                    className="variant-label label-show-all enabled position-relative8 cursor-pointer"
                    onClick={() => {
                        setShowAll(true);
                    }}
                    data-uk-tooltip={t('documents:document.navbar.main.tooltips.showAllVariants')}
                >
                    {t('documents:document.navbar.main.badges.plusNumberOfVariants', {
                        count: areaDocumentVariants.length - limit,
                    })}
                </span>
            )}

            {blockVariants.length <= 1 && !isTemplate && (
                <div className="small flex-center text-danger">
                    <ExclamationTriangleFill size={14} className="flex-shrink-0 mr-1" />
                    {t('documents:document.navbar.main.badges.noVariants')}
                </div>
            )}
        </div>
    );

    function toggleVariant(variantId) {
        setSelectedVariants(xor(selectedVariants, [toString(variantId)]));
    }
}
